import Vue from 'vue'
import Vuex from 'vuex'
import _ from 'lodash'
import createPersistedState from 'vuex-persistedstate'
import ui from '@/store/ui'
import currentPage from '@/store/current-page'
import responsive from '@/store/responsive'
import fractions from '@/store/fractions'
import commerce from '@/store/commerce'
import checkout from '@/store/checkout'
import favorites from '@/store/favorites'
import productVariants from '@/store/product-variants'
import myMachines from '@/store/my-machines'
import myInvoiceHistory from '@/store/my-invoice-history'
import myUsers from '@/store/my-users'
import myShoppingLists from '@/store/my-shopping-lists'
import myDownloads from '@/store/my-downloads'
import myDeliveryAddresses from '@/store/my-delivery-addresses'
import chat, { endSessionEndedInOtherTab } from '@/store/chat'

Vue.use(Vuex)

const LOCAL_STORAGE_KEY = 'vuex'
const localStoragePaths = ['ui.isCartDocked', 'ui.isCartOpen', 'ui.useVat', 'ui.hasAcceptedCookies', 'ui.shownMessages', 'ui.previousMarket', 'ui.showDesktopFilter', 'chat.messages', 'chat.sessionId', 'chat.username', 'chat.ownerAuthenticated']

// Only these session paths are also written directly by the Vue3 storefront (bypassing this
// store entirely), so only these need the same-tab rehydration below.
const vue3SharedSessionPaths = ['myMachines.globalMachineOwnerSelection', 'myMachines.globalMachineSelection']

/*
 * Keeps this store from clobbering a write the Vue3 storefront made directly to sessionStorage.
 *
 * Unlike the cross-tab localStorage case below, there is no 'storage' event to catch this with:
 * sessionStorage changes never fire that event at all, not even in the same tab that made them.
 * So instead, on every mutation, this re-reads sessionStorage and merges in whatever it finds for
 * the shared paths before the persisted-state plugin (registered right after this one) runs its
 * own subscriber for that same mutation and serializes state back out - merging first means that
 * write captures the merged value instead of this store's stale copy.
 */
function rehydrateSessionFromVue3(store) {
  const readSaved = () => {
    try {
      const raw = window.sessionStorage.getItem(LOCAL_STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (error) {
      return null // A malformed blob is not worth tearing the store down over.
    }
  }

  // What storage held for each shared path the last time this store looked at (or wrote) it.
  // Storage differing from this means someone else (Vue3) wrote it; storage matching it means any
  // difference from the store is this store's own fresh change, which must NOT be overwritten.
  const lastSeen = {}
  const initial = readSaved() || {}
  vue3SharedSessionPaths.forEach(path => { lastSeen[path] = _.cloneDeep(_.get(initial, path)) })

  const rehydrate = () => {
    const saved = readSaved()
    if (!saved) return

    // Assign only the shared paths, in place. Replacing the whole state (replaceState) swaps every
    // array for a new reference, which spuriously fires unrelated watchers (e.g. notifications).
    // These paths already exist on the state (initialised to null), so the assignment is reactive.
    vue3SharedSessionPaths.forEach(path => {
      const inStorage = _.get(saved, path)
      if (!_.isEqual(inStorage, lastSeen[path])) {
        if (inStorage !== undefined) _.set(store.state, path, inStorage)
        lastSeen[path] = _.cloneDeep(inStorage)
      } else {
        // Unchanged externally: the store's value is current, and the persisted-state plugin is
        // about to write it out.
        lastSeen[path] = _.cloneDeep(_.get(store.state, path))
      }
    })
  }

  store.subscribe(rehydrate)
  // Vue3 announces its own writes (see setPaths in useVue2Store.ts); without this the Vue2 store
  // would only notice them on the next unrelated mutation, leaving e.g. the selection banner stale.
  window.addEventListener('oie:session-storage-updated', rehydrate)
}

const store = new Vuex.Store({
  plugins: [
    // Must run before the sessionStorage persisted-state plugin below: it merges in whatever
    // the Vue3 storefront most recently wrote, so that plugin's own subscriber (next, for the
    // same mutation) serializes the merged value instead of clobbering it. See
    // rehydrateSessionFromVue3 for why this can't just be a 'storage' event listener.
    rehydrateSessionFromVue3,
    createPersistedState({
      paths: localStoragePaths
    }),
    createPersistedState({
      storage: window.sessionStorage,
      paths: [
        'checkout.checkout',
        'checkout.isOrderRequestVisitor',
        'checkout.activeSteps',
        'favorites.favoriteVehicles',
        'ui.useListView',
        'ui.forceView',
        'myMachines.currentMachine',
        'myMachines.currentMachineOwner',
        ...vue3SharedSessionPaths,
        'myInvoiceHistory.currentInvoice',
        'myUsers.currentUser',
        'myShoppingLists.selectedShoppingListId'
      ]
    }),
    endSessionEndedInOtherTab
  ],
  modules: {
    ui,
    currentPage,
    responsive,
    commerce,
    fractions,
    checkout,
    favorites,
    productVariants,
    myMachines,
    myInvoiceHistory,
    myUsers,
    myShoppingLists,
    myDownloads,
    myDeliveryAddresses,
    chat
  }
})

/*
 * Picks up changes another document made to the persisted state.
 *
 * vuex-persistedstate only reads storage once, at startup, and then rewrites the whole persisted
 * subset on every mutation. So a tab that stays open keeps its own copy indefinitely and will
 * eventually write it over anything another document stored - the next mutation is enough, even
 * an unrelated one such as opening the cart drawer.
 *
 * That matters while the Vue3 storefront is live, because it shares this key: without this
 * listener a Vue3 change survives only until some long-lived Vue2 tab happens to mutate. It also
 * keeps two Vue2 tabs in step with each other, which they previously were not.
 */
function rehydrateFromOtherDocument(event) {
  // Both persisted stores use the key 'vuex', so the storage area has to be checked too.
  if (event.storageArea !== window.localStorage) return
  if (event.key !== null && event.key !== LOCAL_STORAGE_KEY) return

  let saved
  try {
    saved = event.newValue ? JSON.parse(event.newValue) : {}
  } catch (error) {
    return // A malformed blob is not worth tearing the store down over.
  }

  const next = _.cloneDeep(store.state)
  let changed = false

  localStoragePaths.forEach(path => {
    const incoming = _.get(saved, path)
    if (incoming !== undefined && !_.isEqual(incoming, _.get(next, path))) {
      _.set(next, path, incoming)
      changed = true
    }
  })

  // replaceState does not notify mutation subscribers, so this cannot bounce back out as another
  // write and start a loop between two tabs.
  if (changed) store.replaceState(next)
}

window.addEventListener('storage', rehydrateFromOtherDocument)

const urlParams = new URLSearchParams(window.location.search)
const vat = urlParams.get('vat')
const view = urlParams.get('view')

if (vat && (vat === '0' || vat === '1')) {
  store.commit('ui/toggle_value', { key: 'useVat', data: vat === '1' })
}

if (view && (view.toLowerCase() === 'grid' || view.toLowerCase() === 'list')) {
  store.commit('ui/toggle_value', { key: 'forceView', data: view.toLowerCase() })
} else {
  store.commit('ui/toggle_value', { key: 'forceView', data: null })
}

export default store
